/**
 * Feature Flags Service
 * Evaluates feature flags based on the environment and overrides.
 */

const isLocal = false && window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Define the default states for each feature flag here.
// In a full implementation, these might be fetched from an API or env variables.
const FLAGS = {
  // Full-screen "coming soon" page shown on every route instead of the app.
  // Bypass by opening any URL with ?ff_COMING_SOON=false (remembered in this
  // browser; undo with ?ff_COMING_SOON=reset).
  COMING_SOON: false,
  TRAINER_MODE: false,
  RATING_MENU: false,
  LEADERBOARD_MENU: true,
  PROFILE_EVENTS: false,
  PROFILE_GAMES: false,
  // Home page
  HOME_AD_BANNER: false,
  HOME_RANDOM_QUESTION: true,
  HOME_TIMER_CARD: true,
  HOME_BUZZER_CARD: true,
  HOME_SHEET_CARD: false,
  HOME_PARTNERS: false,
  // "Tezliklə" ticker announcing upcoming features
  HOME_COMING_SOON_FEED: true,
  // Package page
  PACKAGE_AD_BANNER: false,
  // Rating / comments / share bar on questions and themes
  QUESTION_ENGAGEMENT_BAR: false,
  // Profile "Paket göndər" submission button + dialog
  SUBMIT_PACKAGE: false,
  // Profile "Komandalar" (teams + invitations) card
  PROFILE_TEAMS: false,
  // Profile rated questions + comments activity card (own profile only)
  PROFILE_ACTIVITY: false,
  MOBILE_APP: false,
  // Apelyasiya meneceri: /tournament-panel/appeals route + its /tools and header entries
  TOURNAMENT_PANEL: true,
  // Account verification: request card, reference requests, dialog and route
  ACCOUNT_VERIFICATION: false,
  // User accounts: login, register, password reset, account confirmation,
  // profile routes and every header/CTA entry point into them
  USER_ACCOUNTS: false,
  // Package order feature
  PACKAGE_ORDER: false,
  // Random package: home card + /packages/random page (redirects home when off)
  RANDOM_PACKAGE: true,
  // Buzzer point counter (Xəmsə-style scoring) on the host screen
  BUZZER_SCORING: false,
  // "Cədvəli izlə" Google Sheets viewer: /sheets route + its /tools, header and home entries (redirects home when off)
  SHEET_VIEWER: false,
  // Standalone point counter: home card + /scoring page (redirects home when off)
  SCORING_BOARD: true,
  // Clickable package tags + /tags/:id paginated package list (redirects home when off)
  PACKAGE_TAG_PAGES: false,
  // "Funksiya təklif et" footer link + dialog (saved to Contacts as feature_request)
  FEATURE_REQUEST: true,
  // "İnisiallar" name game: /initials route + its /tools and header entries (redirects home when off)
  INITIALS_GAME: false,
  // Bottom-left "Yenilik" card announcing a release (content + deadline in const/release.ts)
  RELEASE_BANNER: true,
};

export type FlagName = keyof typeof FLAGS;

const storageKey = (flagName: string) => `ff_${flagName}`;

const readOverride = (flagName: FlagName): string | null => {
  try {
    return localStorage.getItem(storageKey(flagName));
  } catch {
    return null;
  }
};

/**
 * Applies `?ff_<FLAG>=true|false|reset` query params (`1`/`0` also work) by
 * saving them as localStorage overrides. They are persisted because in-app
 * navigation drops the query string. Runs once when this module loads.
 */
const applyQueryOverrides = () => {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return;
  }
  params.forEach((value, key) => {
    if (!key.startsWith('ff_') || !(key.slice(3) in FLAGS)) return;
    const v = value.trim().toLowerCase();
    try {
      if (v === 'true' || v === '1') localStorage.setItem(key, 'true');
      else if (v === 'false' || v === '0') localStorage.setItem(key, 'false');
      else if (v === 'reset') localStorage.removeItem(key);
    } catch {
      // storage unavailable (private mode etc.) — the override just won't stick
    }
  });
};

applyQueryOverrides();

/**
 * Checks if a specific feature flag is completely enabled.
 * - Always visible on local environments
 * - Otherwise falls back to the defined default OR a localStorage override (e.g. ff_TRAINER_MODE=true),
 *   which can also be set from the URL: ?ff_TRAINER_MODE=true
 */
export const isFeatureEnabled = (flagName: FlagName): boolean => {
  if (isLocal) {
    return true; // Always visible on local
  }

  // Allow QA/manual overrides via localStorage for non-local environments
  const localOverride = readOverride(flagName);
  if (localOverride !== null) {
    return localOverride === 'true';
  }

  return FLAGS[flagName];
};
